import { Component, OnInit, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PassagerService } from '../../../core/services/passager/passager.service';
import { DestinationService } from '../../../core/services/destination/destination.service';
import { ToastrService } from 'ngx-toastr';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { UserOptions } from 'jspdf-autotable';
import { 
  checkPassengerConsistency, 
  isPassengerCompatible, 
  NameConsistencyResult 
} from '../../../core/utils/name-similarity.util';

interface jsPDFWithPlugin extends jsPDF {
  autoTable: (options: UserOptions) => jsPDF;
}

@Component({
  selector: 'app-cardmodal',
  templateUrl: './cardmodal.component.html',
  styleUrls: ['./cardmodal.component.css']
})
export class CardmodalComponent implements OnInit {

  dataSource = new MatTableDataSource();
  displayedColumns = [
    'id', 
    'ticket', 
    'passager_info', 
    'voyage_info', 
    'tarif', 
    'action'
  ];
  readonly maxBasketSize = 10;

  card: any[] = [];
  isValidating: boolean = false;
  userprivileges: any;
  sort: any;
  agenceCode: string = '';

  // Passenger Name Consistency Result
  nameConsistency: NameConsistencyResult = {
    isConsistent: true,
    score: 100,
    status: 'exact',
    primaryName: '',
    primaryCni: '',
    primaryPhone: '',
    distinctNames: [],
    conflictingTickets: [],
    message: ''
  };

  // Manual tickets compensation support
  manualTicketsCount: number = 0;
  manualTicketsReferences: string = '';

  // Compensation details
  beneficiaryName: string = '';
  beneficiaryPhone: string = '';
  beneficiaryCni: string = '';
  selectedDestinationName: string = '';
  selectedDestinationId: string = '';
  selectedCategory: string = 'Classique';
  validityDays: number = 60;
  estimatedValue: number = 0;
  comment: string = '';

  // Statistics
  totalAmount: number = 0;
  averageAmount: number = 0;

  destinations: any[] = [];

  constructor(
    private passengerService: PassagerService,
    private destinationService: DestinationService,
    private dialogref: MatDialogRef<CardmodalComponent>,
    @Inject(MAT_DIALOG_DATA) public modalData: any,
    private toastr: ToastrService,
    private datePipe: DatePipe,
    public translate: TranslateService
  ) {
    this.userprivileges = localStorage.getItem('authority');
    try {
      this.userprivileges = JSON.parse(this.userprivileges);
    } catch (e) {
      this.userprivileges = [];
    }
    this.sort = new MatSort();
    const agence: any = localStorage.getItem('agenceinso');
    if (agence) {
      const agenceData = JSON.parse(agence);
      this.agenceCode = agenceData?.codeagence || '';
    }
  }

  ngOnInit(): void {
    this.loadDestinations();
    this.getcardContain();
  }

  loadDestinations(): void {
    this.destinationService.getdestinations().subscribe({
      next: (data: any) => {
        this.destinations = Array.isArray(data) ? data : (data?.data || []);
      },
      error: () => {}
    });
  }

  getcardContain(): void {
    let cardcontain: any = localStorage.getItem('card');
    cardcontain = cardcontain ? JSON.parse(cardcontain) : [];
    this.card = cardcontain;
    this.dataSource = new MatTableDataSource(this.card);
    this.dataSource.sort = this.sort;

    // Set default manual count so total equals 10 if < 10
    if (this.card.length > 0 && this.card.length < this.maxBasketSize) {
      this.manualTicketsCount = this.maxBasketSize - this.card.length;
    } else {
      this.manualTicketsCount = 0;
    }

    this.calculateStats();
  }

  calculateStats(): void {
    if (this.card.length === 0) {
      this.totalAmount = 0;
      this.averageAmount = 0;
      return;
    }

    let sum = 0;
    const destCounts: { [key: string]: number } = {};
    const catCounts: { [key: string]: number } = {};

    this.card.forEach((t: any) => {
      const m = parseFloat(t.montant) || 0;
      sum += m;

      const dest = t.destination?.nomdest || '';
      if (dest) destCounts[dest] = (destCounts[dest] || 0) + 1;

      const cat = t.voyage?.category?.nomcategory || 'Classique';
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });

    this.totalAmount = sum;
    this.averageAmount = Math.round(sum / this.card.length);
    this.estimatedValue = this.averageAmount;

    // Determine top destination
    let topDest = '';
    let topDestMax = 0;
    Object.keys(destCounts).forEach(d => {
      if (destCounts[d] > topDestMax) {
        topDestMax = destCounts[d];
        topDest = d;
      }
    });
    this.selectedDestinationName = topDest || (this.card[0]?.destination?.nomdest || '');

    // Determine top category
    let topCat = 'Classique';
    let topCatMax = 0;
    Object.keys(catCounts).forEach(c => {
      if (catCounts[c] > topCatMax) {
        topCatMax = catCounts[c];
        topCat = c;
      }
    });
    this.selectedCategory = topCat;

    // Beneficiary info from first ticket
    const p = this.card[0]?.passager;
    if (p) {
      this.beneficiaryName = p.nompassager || '';
      this.beneficiaryPhone = p.contact || '';
      this.beneficiaryCni = p.cni || '';
    }

    // Evaluate passenger name consistency across all tickets in basket
    this.nameConsistency = checkPassengerConsistency(this.card, this.beneficiaryName);
  }

  cleanBasketToPrimary(): void {
    if (!this.nameConsistency || this.card.length === 0) return;
    const primary = this.card[0]?.passager || {};
    this.card = this.card.filter((t: any) => isPassengerCompatible(primary, t.passager || {}).compatible);
    if (this.card.length > 0) {
      localStorage.setItem('card', JSON.stringify(this.card));
    } else {
      localStorage.removeItem('card');
    }
    this.dataSource = new MatTableDataSource(this.card);
    this.dataSource.sort = this.sort;
    if (this.card.length < this.maxBasketSize) {
      this.manualTicketsCount = this.maxBasketSize - this.card.length;
    }
    this.calculateStats();
    this.toastr.info(
      `Le panier a été harmonisé : seuls les billets de "${this.nameConsistency.primaryName}" ont été conservés.`,
      'Panier harmonisé'
    );
  }

  getTotalTicketCount(): number {
    return (this.card?.length || 0) + (this.manualTicketsCount || 0);
  }

  close(): void {
    this.dialogref.close();
  }

  empty(): void {
    localStorage.removeItem('card');
    this.card = [];
    this.dialogref.close();
  }

  remove(data: any): void {
    const index = this.card.indexOf(data);
    if (index > -1) {
      this.card.splice(index, 1);
      if (this.card.length > 0) {
        localStorage.setItem('card', JSON.stringify(this.card));
      } else {
        localStorage.removeItem('card');
      }
      this.dataSource = new MatTableDataSource(this.card);
      this.dataSource.sort = this.sort;
      if (this.card.length < this.maxBasketSize) {
        this.manualTicketsCount = this.maxBasketSize - this.card.length;
      }
      this.calculateStats();
    }
  }

  get zeroPriceTicketsCount(): number {
    return this.card ? this.card.filter((t: any) => {
      const m = typeof t.montant === 'number' ? t.montant : parseFloat(String(t.montant).trim());
      return isNaN(m) || m <= 0;
    }).length : 0;
  }

  removeZeroPriceTickets(): void {
    if (!this.card || this.card.length === 0) return;
    const initialCount = this.card.length;
    this.card = this.card.filter((t: any) => {
      const m = typeof t.montant === 'number' ? t.montant : parseFloat(String(t.montant).trim());
      return !isNaN(m) && m > 0;
    });
    const removed = initialCount - this.card.length;
    if (this.card.length > 0) {
      localStorage.setItem('card', JSON.stringify(this.card));
    } else {
      localStorage.removeItem('card');
    }
    this.dataSource = new MatTableDataSource(this.card);
    this.dataSource.sort = this.sort;
    if (this.card.length < this.maxBasketSize) {
      this.manualTicketsCount = this.maxBasketSize - this.card.length;
    }
    this.calculateStats();
    this.toastr.info(`${removed} billet(s) à 0 FCFA retiré(s) du panier.`, 'Panier mis à jour');
  }

  validateCompensation(): void {
    const digitalCount = this.card.length;
    const manualCount = this.manualTicketsCount || 0;
    const totalCount = digitalCount + manualCount;

    if (digitalCount === 0) {
      this.toastr.warning('Veuillez ajouter au moins un billet numérique du VPS au panier.', 'Panier vide');
      return;
    }

    // Verify that all tickets have a price > 0
    if (this.zeroPriceTicketsCount > 0) {
      this.toastr.error(
        `Validation impossible : ${this.zeroPriceTicketsCount} billet(s) ont un montant de 0 FCFA. Seuls les billets payants sont éligibles au programme de fidélité.`,
        'Billets non payants détectés'
      );
      return;
    }

    // Verify name consistency before allowing validation
    if (!this.nameConsistency.isConsistent) {
      this.toastr.error(
        `Validation impossible : Les billets du panier appartiennent à des passagers différents (${this.nameConsistency.distinctNames.join(' vs ')}). Veuillez harmoniser le panier avant de continuer.`,
        'Incohérence Passagers'
      );
      return;
    }

    if (totalCount > this.maxBasketSize) {
      this.toastr.warning(`Le nombre total de billets ne peut pas dépasser ${this.maxBasketSize}.`, 'Quota dépassé');
      return;
    }

    if (manualCount > 0 && (!this.manualTicketsReferences || !this.manualTicketsReferences.trim())) {
      this.toastr.warning(
        'Veuillez saisir les références ou numéros des billets manuels papier vérifiés.',
        'Billets manuels requis'
      );
      return;
    }

    const ticketIds = this.card.map((t: any) => t.idpv);

    const isFr = (this.translate.currentLang || 'fr') === 'fr';
    const confirmTitle = this.translate.instant('LOYALTY.CONFIRM_TITLE') || (isFr ? 'Confirmer la compensation' : 'Confirm Compensation');
    const validateBtnText = this.translate.instant('LOYALTY.VALIDATE') || (isFr ? 'Valider' : 'Validate');
    const cancelBtnText = this.translate.instant('LOYALTY.CANCEL') || (isFr ? 'Annuler' : 'Cancel');

    const beneficiaryLabel = this.translate.instant('LOYALTY.BENEFICIARY') || (isFr ? 'Bénéficiaire' : 'Beneficiary');
    const validatedTicketsLabel = this.translate.instant('LOYALTY.VALIDATED_TICKETS') || (isFr ? 'Billets validés' : 'Validated tickets');
    const digitalLabel = this.translate.instant('LOYALTY.DIGITAL') || (isFr ? 'numérique(s)' : 'digital');
    const manualLabel = this.translate.instant('LOYALTY.MANUAL') || (isFr ? 'manuel(s)' : 'manual');
    const totalLabel = this.translate.instant('LOYALTY.TOTAL') || (isFr ? 'Total' : 'Total');
    const offeredDestLabel = this.translate.instant('LOYALTY.OFFERED_DEST') || (isFr ? 'Destination offerte' : 'Offered destination');
    const priceLabel = this.translate.instant('LOYALTY.FREE_TICKET_PRICE') || (isFr ? 'Prix du billet gratuit' : 'Free ticket price');
    const freeBadge = this.translate.instant('LOYALTY.FREE_LOYALTY') || (isFr ? '0 FCFA (Gratuité Fidélité)' : '0 FCFA (Loyalty Free Ticket)');
    const warningText = isFr
      ? `⚠️ Les ${digitalCount} billet(s) numérique(s) seront définitivement annulés et marqués comme utilisés sur le serveur central.`
      : `⚠️ The ${digitalCount} digital ticket(s) will be permanently cancelled and marked as used on the central server.`;

    Swal.fire({
      title: confirmTitle,
      html: `
        <div style="text-align: left; font-size: 14px; line-height: 1.7; color: #37474f;">
          <p style="margin-bottom: 6px;"><strong>${beneficiaryLabel} :</strong> ${this.beneficiaryName} (CNI: ${this.beneficiaryCni || '-'})</p>
          <p style="margin-bottom: 6px;"><strong>${validatedTicketsLabel} :</strong> ${digitalCount} ${digitalLabel} + ${manualCount} ${manualLabel} (${totalLabel} : <strong>${totalCount}/${this.maxBasketSize}</strong>)</p>
          <p style="margin-bottom: 6px;"><strong>${offeredDestLabel} :</strong> ${this.selectedDestinationName} (${this.selectedCategory})</p>
          <p style="margin-bottom: 6px;"><strong>${priceLabel} :</strong> <span style="color: #1565c0; font-weight: bold;">${freeBadge}</span></p>
          <p style="color: #d32f2f; font-weight: 600; margin-top: 12px; font-size: 13px;">
            ${warningText}
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: validateBtnText,
      cancelButtonText: cancelBtnText,
      buttonsStyling: false,
      customClass: {
        popup: 'gev-swal-popup',
        confirmButton: 'gev-swal-confirm-btn',
        cancelButton: 'gev-swal-cancel-btn',
        actions: 'gev-swal-actions'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.isValidating = true;

        const payload = {
          ticketIds: ticketIds,
          manualTicketsCount: manualCount,
          manualTicketsReferences: this.manualTicketsReferences?.trim() || null,
          passengerName: this.beneficiaryName?.trim() || null,
          passengerPhone: this.beneficiaryPhone?.trim() || null,
          passengerCni: this.beneficiaryCni?.trim() || null,
          destinationOfferteName: this.selectedDestinationName || null,
          categoryOfferte: this.selectedCategory || 'Classique',
          valeurEstimee: 0,
          validityDays: this.validityDays || 60,
          agencyCode: this.agenceCode,
          comment: this.comment?.trim() || null
        };

        this.passengerService.compensatePassenger(payload).subscribe({
          next: (response: any) => {
            this.isValidating = false;
            const freeCode = response.freeTicketCode;
            const expDate = response.expirationDate;

            // Clear basket from localStorage
            this.card = [];
            localStorage.removeItem('card');

            const successTitle = this.translate.instant('LOYALTY.SUCCESS_TITLE') || (isFr ? 'Compensation Validée !' : 'Compensation Validated!');
            const codeLabel = this.translate.instant('LOYALTY.FREE_CODE_LABEL') || (isFr ? 'Code de Gratuité Généré (Serveur Central)' : 'Generated Free Ticket Code (Central Server)');
            const printBtnText = this.translate.instant('LOYALTY.PRINT_VOUCHER') || (isFr ? 'Imprimer le Bon (PDF)' : 'Print Voucher (PDF)');
            const closeBtnText = this.translate.instant('LOYALTY.CLOSE') || (isFr ? 'Fermer' : 'Close');
            const destLabel = this.translate.instant('LOYALTY.OFFERED_DEST') || (isFr ? 'Destination' : 'Destination');
            const priceTitle = this.translate.instant('LOYALTY.FREE_TICKET_PRICE') || (isFr ? 'Prix du Billet' : 'Ticket Price');
            const validityLabel = this.translate.instant('LOYALTY.VALIDITY') || (isFr ? 'Validité' : 'Validity');
            const untilLabel = this.translate.instant('LOYALTY.UNTIL') || (isFr ? "Jusqu'au" : 'Until');

            Swal.fire({
              title: successTitle,
              html: `
                <div style="text-align: center; padding: 10px;">
                  <div style="font-size: 13px; color: #555; margin-bottom: 6px;">${codeLabel}</div>
                  <div style="font-size: 24px; font-weight: bold; letter-spacing: 2px; color: #0d47a1; background: #e3f2fd; padding: 14px; border-radius: 8px; border: 2px dashed #1976d2; user-select: all;">
                    ${freeCode}
                  </div>
                  <div style="margin-top: 14px; font-size: 13px; color: #333; line-height: 1.7;">
                    <strong>${beneficiaryLabel} :</strong> ${response.passengerName || this.beneficiaryName}<br>
                    <strong>${destLabel} :</strong> ${response.destinationOfferte || this.selectedDestinationName} (${response.categoryOfferte || this.selectedCategory})<br>
                    <strong>${priceTitle} :</strong> <span style="color: #1565c0; font-weight: bold;">0 FCFA</span><br>
                    <strong>${validityLabel} :</strong> ${untilLabel} ${this.datePipe.transform(expDate, 'dd/MM/yyyy HH:mm')}
                  </div>
                </div>
              `,
              icon: 'success',
              showCancelButton: true,
              confirmButtonText: printBtnText,
              cancelButtonText: closeBtnText,
              buttonsStyling: false,
              customClass: {
                popup: 'gev-swal-popup',
                confirmButton: 'gev-swal-confirm-btn',
                cancelButton: 'gev-swal-cancel-btn',
                actions: 'gev-swal-actions'
              }
            }).then((modalRes) => {
              if (modalRes.isConfirmed) {
                this.printFreeTicketVoucher(response);
              }
              this.dialogref.close({ validated: true, code: freeCode });
            });
          },
          error: (error: any) => {
            this.isValidating = false;
            console.error('Error in compensatePassenger:', error);
            const msg = error.error?.message || 'Erreur lors de la validation sur le serveur central VPS.';
            this.toastr.error(msg, 'Échec de Validation');
          }
        });
      }
    });
  }

  printFreeTicketVoucher(data: any): void {
    const doc = new jsPDF('p', 'mm', 'a4') as jsPDFWithPlugin;

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 35, 126);
    doc.text('GENERAL EXPRESS VOYAGES', 105, 20, { align: 'center' });

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(46, 125, 50);
    doc.text('ATTESTATION & BON DE BILLET GRATUIT — PROGRAMME FIDÉLITÉ', 105, 28, { align: 'center' });

    doc.setLineWidth(0.5);
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 33, 195, 33);

    // Voucher Code Box
    doc.setFillColor(232, 245, 233);
    doc.roundedRect(25, 38, 160, 24, 4, 4, 'F');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text('CODE DU BON DE GRATUITÉ (À PRÉSENTER AU GUICHET) :', 105, 44, { align: 'center' });
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(27, 94, 32);
    doc.text(data.freeTicketCode || '-', 105, 52, { align: 'center' });
    if (data.freeTicketId) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Réf. Dossier : ${data.freeTicketId}`, 105, 58, { align: 'center' });
    }

    // Passenger and Trip Details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);

    const leftCol = 20;
    const rightCol = 110;
    let y = 70;

    doc.text(`Bénéficiaire : ${data.passengerName || this.beneficiaryName || '-'}`, leftCol, y);
    doc.text(`Agence d'émission : ${this.agenceCode || 'SVR'}`, rightCol, y);
    y += 7;

    doc.text(`CNI / Identifiant : ${data.passengerCni || this.beneficiaryCni || '-'}`, leftCol, y);
    doc.text(`Date d'émission : ${this.datePipe.transform(new Date(), 'dd-MM-yyyy HH:mm')}`, rightCol, y);
    y += 7;

    doc.text(`N° Téléphone : ${data.passengerPhone || this.beneficiaryPhone || '-'}`, leftCol, y);
    doc.text(`Date d'expiration : ${this.datePipe.transform(data.expirationDate, 'dd-MM-yyyy') || '-'}`, rightCol, y);
    y += 7;

    doc.text(`Destination Offerte : ${data.destinationOfferte || this.selectedDestinationName || '-'}`, leftCol, y);
    doc.text(`Classe accordée : ${data.categoryOfferte || this.selectedCategory || 'Classique'}`, rightCol, y);
    y += 7;

    doc.text(`Prix du Billet : 0 FCFA (Gratuité Fidélité)`, leftCol, y);
    doc.text(`Total billets validés : ${data.totalTicketsCount || this.getTotalTicketCount()} / 10`, rightCol, y);
    y += 12;

    // Table of cancelled digital tickets
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 35, 126);
    doc.text('Détail des billets numériques annulés sur le serveur central VPS :', 20, y);
    y += 4;

    const tableData: any[] = [];
    (data.validatedTicketIds || []).forEach((id: string, idx: number) => {
      const orig = this.card.find((t: any) => t.idpv === id);
      tableData.push([
        idx + 1,
        orig?.ticket2Display || orig?.idpvDisplay || id,
        orig?.voyage?.agencedepart || '-',
        orig?.destination?.nomdest || '-',
        this.datePipe.transform(orig?.voyage?.datevoyage || orig?.creationdate, 'dd-MM-yyyy') || '-',
        (orig?.montant || 0) + ' FCFA',
        'ANNULÉ POUR FIDÉLITÉ'
      ]);
    });

    if (data.manualTicketsCount > 0) {
      tableData.push([
        '+',
        `Billets manuels papier (${data.manualTicketsCount})`,
        'Vérifiés au guichet',
        this.manualTicketsReferences || '-',
        'Année en cours',
        '-',
        'CONFORMITÉ PAPIER VALIDÉE'
      ]);
    }

    doc.autoTable({
      head: [['N°', 'Référence Billet', 'Origine', 'Destination', 'Date', 'Montant', 'Statut VPS']],
      body: tableData,
      startY: y,
      theme: 'grid',
      headStyles: {
        fillColor: [26, 35, 126],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: { fontSize: 8, cellPadding: 2, textColor: [40, 40, 40] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'left', cellWidth: 38 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'left', cellWidth: 32 },
        4: { halign: 'center', cellWidth: 22 },
        5: { halign: 'right', cellWidth: 25 },
        6: { halign: 'center', cellWidth: 38 }
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY || 160;

    // Signatures box
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Visa du Guichetier / Chef d\'Agence', 30, finalY + 20);
    doc.text('Signature du Bénéficiaire', 135, finalY + 20);

    doc.setLineWidth(0.3);
    doc.line(30, finalY + 38, 85, finalY + 38);
    doc.line(135, finalY + 38, 185, finalY + 38);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'italic');
    doc.text(
      'Ce bon donne droit à un billet gratuit aller-simple dans la limite de validité et selon les conditions de General Express Voyages.',
      105,
      finalY + 45,
      { align: 'center' }
    );

    window.open(doc.output('bloburl').toString(), '_blank');
  }

}
